import { createStyles, Grid, makeStyles, Toolbar } from "@material-ui/core";
import qs from "qs";
import * as React from "react";

import {
    domain,
    Link,
    LocationOption,
    LocationSearch,
    MarketResearchSearchCriteria,
    NormalizedJobTitleOption,
    NormalizedJobTitleSearch,
    RelodeTheme,
    Text,
    useDeviceSize,
} from "../../..";

const useMarketResearchConfigurationSectionStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            keywordInput: {
                margin: theme.spacing(1, 0, 2, 0),
            },
            locationInput: {
                margin: theme.spacing(1, 0, 2, 0),
            },
            recentConfigItem: {
                marginBottom: theme.spacing(2),
            },
            recentHeading: {
                marginTop: theme.spacing(2),
            },
            recentConfigItemCaption: {
                color: theme.palette.grey[600],
            },
        }),
    { name: "MarketResearchConfigurationSection" }
);

type MarketResearchConfigurationSectionProps = {
    searchCriteria: MarketResearchSearchCriteria;

    onJobTitleChange: (
        item?: never,
        normalizedJobTitle?: domain.NormalizedJobTitle
    ) => void;
    onLocationFilter: (location: LocationOption) => void;
    searchAction?: React.ReactNode;
    recentSearches?: MarketResearchSearchCriteria[];
};

export const MarketResearchConfigurationSection = (
    props: MarketResearchConfigurationSectionProps
) => {
    const classes = useMarketResearchConfigurationSectionStyles(props);
    const device = useDeviceSize();

    const {
        searchAction,
        onLocationFilter,
        onJobTitleChange,
        searchCriteria,
        recentSearches = [],
    } = props;

    const locationSuggestions: domain.Location[] = [
        {
            locationName: "Remote/National",
            locationId: 5,
        },
    ];

    const renderRecentSearch = (
        item: MarketResearchSearchCriteria,
        index: number
    ) => {
        const searchCriteria: MarketResearchSearchCriteria = {
            jobName: item.jobName,
            locationName: item.locationName,
            locationId: item.locationId,
        };

        const queryString = qs.stringify(searchCriteria, {
            skipNulls: true,
            addQueryPrefix: true,
            sort: (a, b) => a.localeCompare(b),
        });

        return (
            <Grid key={index} className={classes.recentConfigItem} container>
                <Grid item xs={12}>
                    <Link
                        href={`/${appConfig.appName}/market-research${queryString}`}
                    >
                        <Text variant="subtitle1">{item.jobName}</Text>
                    </Link>
                </Grid>
                <Grid item xs={12} className={classes.recentConfigItemCaption}>
                    {item.locationName}
                </Grid>
            </Grid>
        );
    };

    const renderRecentConfigurationSection = () => {
        if (recentSearches.length > 0)
            return (
                <>
                    <Toolbar
                        className={classes.recentHeading}
                        disableGutters={true}
                    >
                        <Text variant="h2">recent</Text>
                    </Toolbar>
                    {recentSearches.map((x, i) => renderRecentSearch(x, i))}
                </>
            );
    };

    const selectedNormalizedJobTitleOption: NormalizedJobTitleOption = !!searchCriteria?.jobName
        ? {
              externalId: "",
              jobName: searchCriteria?.jobName,
              confidencePercentage: 1.0,
              description: "",
          }
        : null;
    const selectedLocationOption: LocationOption = !!searchCriteria?.locationId
        ? {
              locationId: searchCriteria?.locationId,
              locationName: searchCriteria?.locationName,
          }
        : null;

    return (
        <div>
            {device === "desktop" ? (
                <Toolbar disableGutters={true}>
                    <Text variant="h2">criteria</Text>
                </Toolbar>
            ) : null}
            <div>
                <NormalizedJobTitleSearch
                    id="marketResearchConfigurationSectionJobTitleInput"
                    label="job title"
                    fullWidth
                    freeSolo
                    className={classes.keywordInput}
                    onSelect={onJobTitleChange}
                    selectedNormalizedJobTitle={
                        selectedNormalizedJobTitleOption
                    }
                />
                <LocationSearch
                    id="marketResearchConfigurationSectionLocationSearch"
                    label="city, remote, or national"
                    className={classes.locationInput}
                    selectedLocation={selectedLocationOption}
                    onSelect={onLocationFilter}
                    initialSuggestions={locationSuggestions}
                />
            </div>

            {searchAction}
            {renderRecentConfigurationSection()}
        </div>
    );
};
